import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    List,
    ListItem,
    ListItemText,
    Paper,
    Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
    FieldErrors,
    UseFormGetValues,
    UseFormRegister,
    UseFormSetValue,
    UseFormWatch,
} from 'react-hook-form';
import { FileUploadResponse } from '../../../types/asset.types';
import {
    DEFAULT_SONGS_METADATA,
    DEFAULT_STARS_METADATA,
} from '../../../types/referencelayerMetadata';
import { environmentSafeLog } from '../../../utils/environment';
import { compositeLayerStrategy } from '../../../modules/registration/layers/CompositeLayerStrategy';
import CompositeMetadataEditor from '../../common/CompositeMetadataEditor';
import LooksInformation from '../../common/LooksInformation';
import MovesMetadataEditor from '../../common/MovesMetadataEditor';
import SongsMetadataEditor from '../../common/SongsMetadataEditor';
import StarsMetadataEditor from '../../common/StarsMetadataEditor';
import WorldsMetadataEditor from '../../common/WorldsMetadataEditor';
import CompositeAssetSelection from '../CompositeAssetSelection';

// Form data interface (matching RegisterAssetPage)
interface FormData {
  layer: string;
  layerName: string;
  categoryCode: string;
  categoryName: string;
  subcategoryCode: string;
  subcategoryName: string;
  subcategoryNumericCode: string;
  name: string;
  description: string;
  source: string; // Source field from dropdown
  tags: string[];
  // Backend AI tag filtering fields
  aiRawTags?: string[]; // Raw AI tags from backend
  aiFilteredTags?: string[]; // Backend-filtered tags
  aiRemovedTags?: string[]; // Tags removed by backend filtering
  files: File[];
  hfn: string; // human friendly name
  humanFriendlyName?: string; // used by registration context
  mfa: string; // machine friendly address
  sequential: string; // sequential number
  trainingData?: any; // Only for T layer
  layerSpecificData?: {
    components: any[]; // Only for C layer
  };
  // Phase 2A: Album art and enhanced metadata fields
  albumArtUrl?: string;
  albumArtSource?: string;
  albumArtQuality?: string;
  extractedSongData?: any;
  bpm?: number;
  // Phase 2C: Structured song metadata for backend
  songMetadata?: any;
  starMetadata?: any;
  looksMetadata?: any;
  movesMetadata?: any;
  worldsMetadata?: any;
  compositeMetadata?: any;
  aiMetadata?: any;
  // V2.0: Unified layer metadata
  layerMetadata?: any;
}

interface ReviewDetailsStepProps {
  // Form methods from React Hook Form
  register: UseFormRegister<FormData>;
  watch: UseFormWatch<FormData>;
  setValue: UseFormSetValue<FormData>;
  getValues: UseFormGetValues<FormData>;
  errors: FieldErrors<FormData>;

  // Navigation
  onBack: () => void;
  onSubmit: () => void;

  // Status
  isSubmitting?: boolean;
  loading?: boolean;
  submitError?: string;

  // Uploaded files info
  uploadedFiles?: FileUploadResponse[];

  // Layer-specific functions
  getLayerSpecificPlaceholder: (layer: string) => string;

  // Layer type indicators
  isCompositeLayer?: boolean;
  isTrainingLayer?: boolean;
}

/** Normalize form/API tags to string[] so includes/spread work even when stored as object or string. */
function ensureTagsArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((t): t is string => typeof t === 'string');
  }
  if (typeof value === 'string') {
    const s = value.trim();
    return s ? [s] : [];
  }
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return Object.values(value).filter((t): t is string => typeof t === 'string');
  }
  return [];
}

/**
 * Step 4: Review Details
 *
 * This component handles the review and editing of all asset details before submission.
 * It includes:
 * - Editable Creator's Description, Description, and Tags
 * - Song-specific metadata editing (for G layer)
 * - Taxonomy display with three-letter codes
 * - File information display
 * - Navigation and submission controls
 */
const ReviewDetailsStep: React.FC<ReviewDetailsStepProps> = ({
  register,
  watch,
  setValue,
  getValues,
  errors,
  onBack,
  onSubmit,
  isSubmitting = false,
  loading = false,
  submitError,
  uploadedFiles = [],
  getLayerSpecificPlaceholder,
  isCompositeLayer = false,
  isTrainingLayer = false,
}) => {
  const watchLayer = watch('layer');

  // CRITICAL DEBUGGING: Log when component renders
  console.log('🚨🚨🚨 [REVIEW DETAILS STEP] Component rendered!');
  console.log('🚨🚨🚨 [REVIEW DETAILS STEP] watchLayer:', watchLayer);
  console.log('🚨🚨🚨 [REVIEW DETAILS STEP] All props:', {
    watchLayer,
    isSubmitting,
    loading,
  });

  // Base Star Selection Modal State
  const [showBaseStarModal, setShowBaseStarModal] = useState(false);
  const [availableBaseStars] = useState<any[]>([]);
  const [loadingBaseStars] = useState(false);
  const [showCompositeDialog, setShowCompositeDialog] = useState(false);
  const [selectedCompositeAssets, setSelectedCompositeAssets] = useState<any[]>(
    []
  );

  // Seed default composite metadata when on composite Review step and no metadata exists yet.
  // This allows step 4 validation to pass so the user can submit or edit in CompositeMetadataEditor.
  useEffect(() => {
    if (watchLayer !== 'C') return;
    const layerMeta = getValues('layerMetadata');
    const compositeMeta = getValues('compositeMetadata');
    const hasExisting =
      (layerMeta && typeof layerMeta === 'object' && Object.keys(layerMeta).length > 0) ||
      (compositeMeta && typeof compositeMeta === 'object' && Object.keys(compositeMeta).length > 0);
    if (hasExisting) return;
    const defaults = compositeLayerStrategy.getDefaultMetadata();
    const humanFriendlyName = getValues('humanFriendlyName') || getValues('hfn') || getValues('name') || '';
    const compositeName = (humanFriendlyName && String(humanFriendlyName).trim()) || 'Untitled Composite';
    const defaultCompositeMetadata = { ...defaults, compositeName };
    setValue('layerMetadata', defaultCompositeMetadata);
  }, [watchLayer, getValues, setValue]);

  // Debug logging for component state (cleaned up)
  environmentSafeLog(
    '📋 [REVIEW DETAILS] Component mounted - Layer:',
    watchLayer
  );

  // Deployment verification logging and initialization
  React.useEffect(() => {
    if (watchLayer === 'S') {
      environmentSafeLog(
        '🚀 [REVIEW DETAILS] Enhanced Variant UI v2.0 loaded - Asset Type radio buttons should be visible'
      );
      environmentSafeLog(
        '📦 [BUILD INFO] Deployed at:',
        new Date().toISOString()
      );
      environmentSafeLog(
        '✅ [FEATURE CHECK] Radio buttons for Base/Variant selection are enabled'
      );

      // Initialize starMetadata if not present
      const currentStarMetadata = watch('starMetadata');
      if (!currentStarMetadata || !currentStarMetadata.assetType) {
        environmentSafeLog('📝 Initializing starMetadata with default values');
        setValue('starMetadata', {
          ...currentStarMetadata,
          assetType: 'base',
          baseStarId: '',
        });
      }

      // STEP 4 FIX: Populate form fields with rich AI content from starMetadata
      const currentDescription = watch('description');
      const currentTags = ensureTagsArray(watch('tags'));
      const richDescription = currentStarMetadata?.description;
      const richTags = currentStarMetadata?.tags;

      console.log('🔧 [STEP 4 FIX] Checking AI content population:', {
        currentDescription,
        currentTags,
        richDescription,
        richTags,
        hasRichDescription: !!richDescription,
        hasRichTags: !!(richTags && richTags.length > 0),
      });

      // Only update if we have rich content and current content is placeholder/empty
      if (
        richDescription &&
        (currentDescription === 'Generated description' || !currentDescription)
      ) {
        console.log(
          '🚀 [STEP 4 FIX] Populating description field with rich AI content'
        );
        setValue('description', richDescription);
      }

      if (
        richTags &&
        richTags.length > 0 &&
        (!currentTags ||
          currentTags.length === 0 ||
          (currentTags.length === 1 && currentTags[0] === 'general'))
      ) {
        console.log('🚀 [STEP 4 FIX] Populating tags field with rich AI tags');
        setValue('tags', richTags);
      }
    }
  }, [watchLayer, setValue, watch]);

  // Initialize and auto-populate Looks layer metadata
  React.useEffect(() => {
    if (watchLayer === 'L') {
      console.log('🚨🚨🚨 [STEP4 USEEFFECT] Looks layer useEffect triggered!');
      environmentSafeLog(
        '👕 [REVIEW DETAILS] Looks layer detected - initializing looksMetadata'
      );

      const currentLooksMetadata = watch('looksMetadata');

      // CRITICAL FIX: Data is already in currentLooksMetadata from RegisterAssetPage
      // No need to read from aiMetadata - the data flow is: AI → handleAIAdditionalMetadataGenerated → setValue('looksMetadata') → watch('looksMetadata')

      console.log(
        '🔍 [STEP4 DEBUG] currentLooksMetadata (this is where AI data should be):',
        currentLooksMetadata
      );
      console.log(
        '🔍 [STEP4 DEBUG] Has brandNames:',
        !!currentLooksMetadata?.brandNames
      );
      console.log(
        '🔍 [STEP4 DEBUG] Has primaryColors:',
        !!currentLooksMetadata?.primaryColors?.length
      );
      console.log(
        '🔍 [STEP4 DEBUG] Has occasion:',
        currentLooksMetadata?.occasion
      );

      // SIMPLE FIX: Just ensure assetType is set if not present, data is already populated by RegisterAssetPage
      if (currentLooksMetadata && !currentLooksMetadata.assetType) {
        console.log(
          '🔧 [LOOKS FIX] Setting missing assetType to base, preserving AI data'
        );
        setValue('looksMetadata', {
          ...currentLooksMetadata,
          assetType: 'base',
          baseLookId: '',
          variantName: '',
        });
      } else if (!currentLooksMetadata) {
        // Only initialize with defaults if no data exists at all
        console.log(
          '📝 [LOOKS INIT] No looksMetadata found, initializing with defaults'
        );
        setValue('looksMetadata', {
          assetType: 'base',
          baseLookId: '',
          variantName: '',
          styleCategory: '',
          primaryColors: [],
          brandNames: [],
          occasion: '',
        });
      } else {
        console.log(
          '✅ [LOOKS SUCCESS] AI data already populated in looksMetadata by RegisterAssetPage'
        );
      }
    }
  }, [watchLayer, setValue, watch]);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Review & Edit Asset Details
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Review and edit the AI-generated information below before submitting
        your asset.
      </Typography>

      {submitError && (
        <Box sx={{ mb: 3 }}>
          <Typography
            color="error"
            variant="body2"
            sx={{
              p: 2,
              bgcolor: 'error.light',
              borderRadius: 1,
              color: 'error.contrastText',
            }}
          >
            {submitError}
          </Typography>
        </Box>
      )}

      <Grid container spacing={4}>
        {/* Left Column: Editable Asset Metadata */}
        <Grid item xs={12} lg={8}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: 'primary.main', mb: 3 }}
            >
              📝 Asset Metadata
            </Typography>

            {/* AI-Generated Description (Editable) */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{ fontWeight: 600 }}
              >
                Description
              </Typography>
              <textarea
                {...register('description', {
                  maxLength: {
                    value: 1000,
                    message: 'Description must not exceed 1000 characters',
                  },
                })}
                value={watch('description') ?? ''}
                onChange={(e) => setValue('description', e.target.value)}
                placeholder="AI-generated description will appear here"
                rows={4}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                }}
              />
              {errors.description && (
                <Typography
                  color="error"
                  variant="caption"
                  sx={{ display: 'block', mt: 0.5 }}
                >
                  {errors.description.message}
                </Typography>
              )}
            </Box>

            {/* Tags (Editable) */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{ fontWeight: 600 }}
              >
                Tags
              </Typography>
              <Box sx={{ mb: 2 }}>
                {(() => {
                  // Use watched tags for both add handlers and display so adding never overwrites
                  const currentTags = ensureTagsArray(watch('tags'));
                  const aiRawTags = watch('aiRawTags') || [];
                  const aiFilteredTags = watch('aiFilteredTags') || [];
                  const aiRemovedTags = watch('aiRemovedTags') || [];
                  const hasBackendFiltering = aiFilteredTags.length > 0 && aiRawTags.length > 0;

                  return (
                    <>
                {/* Tag Input Field */}
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    p: 1,
                    mb: 1,
                  }}
                >
                  <input
                    id="tag-input"
                    type="text"
                    placeholder="Type a tag and press Enter"
                    style={{
                      border: 'none',
                      outline: 'none',
                      width: '100%',
                      padding: '8px',
                      fontSize: '14px',
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                        e.preventDefault();
                        const currentValue = e.currentTarget.value.trim();
                        if (!currentTags.includes(currentValue)) {
                          setValue('tags', [...currentTags, currentValue]);
                          e.currentTarget.value = '';
                        }
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    sx={{ minWidth: '36px', ml: 1 }}
                    onClick={() => {
                      const input = document.getElementById(
                        'tag-input'
                      ) as HTMLInputElement;
                      if (input && input.value.trim()) {
                        const newTag = input.value.trim();
                        if (!currentTags.includes(newTag)) {
                          setValue('tags', [...currentTags, newTag]);
                          input.value = '';
                        }
                      }
                      input?.focus();
                    }}
                  >
                    +
                  </Button>
                </Box>

                {/* STEP 4 BACKEND-FILTERED TAG DISPLAY */}
                      {/* Backend Filtering Info */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="text.secondary">
                          Step 4: Review and edit final tags
                        </Typography>

                        {hasBackendFiltering ? (
                          <Chip
                            label={`Backend filtered: ${aiRawTags.length} → ${aiFilteredTags.length} tags`}
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{ fontSize: '0.65rem' }}
                          />
                        ) : (
                          <Chip
                            label="Backend filtering not available"
                            size="small"
                            color="info"
                            variant="outlined"
                            sx={{ fontSize: '0.65rem' }}
                          />
                        )}
                      </Box>

                      {/* Tag Display Area */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {currentTags.map((tag, index) => (
                          <Chip
                            key={`tag-${index}`}
                            label={tag}
                            variant="outlined"
                            color="primary"
                            size="small"
                            onDelete={() => {
                              const updatedTags = [...currentTags];
                              updatedTags.splice(index, 1);
                              setValue('tags', updatedTags);
                            }}
                          />
                        ))}
                      </Box>

                      {hasBackendFiltering && aiRemovedTags.length > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                          Backend removed {aiRemovedTags.length} generic tags: {aiRemovedTags.slice(0, 3).join(', ')}{aiRemovedTags.length > 3 ? '...' : ''}
                        </Typography>
                      )}
                    </>
                  );
                })()}
              </Box>
              <Typography variant="caption" color="text.secondary">
                Tags help with searchability and metadata. Edit, add, or remove tags as needed.
              </Typography>
            </Box>

            {/* Songs Layer Enhanced Metadata - Song Information */}
            {watchLayer === 'G' && (
              <>
                <Divider sx={{ my: 3 }} />

                <SongsMetadataEditor
                  layerMetadata={{
                    ...DEFAULT_SONGS_METADATA,
                    ...(typeof watch('layerMetadata') === 'object' &&
                    !Array.isArray(watch('layerMetadata'))
                      ? watch('layerMetadata')
                      : {}),
                    ...(typeof watch('songMetadata') === 'object' &&
                    !Array.isArray(watch('songMetadata'))
                      ? watch('songMetadata')
                      : {}),
                  }}
                  asset={{
                    songMetadata: {
                      ...DEFAULT_SONGS_METADATA,
                      ...(typeof watch('songMetadata') === 'object' &&
                      !Array.isArray(watch('songMetadata'))
                        ? watch('songMetadata')
                        : {}),
                    },
                  }}
                  isEditable={true}
                  onMetadataChange={updatedMetadata => {
                    console.log(
                      '🔧 [REVIEW DETAILS] SongsMetadataEditor metadata changed:',
                      updatedMetadata
                    );
                    // ANTI-BLOAT: For G layer, ONLY set songMetadata (no layerMetadata to prevent dual metadata)
                    setValue('songMetadata', updatedMetadata);
                  }}
                />
              </>
            )}

            {/* Stars Layer Enhanced Metadata - Star Information */}
            {watchLayer === 'S' && (
              <>
                <Divider sx={{ my: 3 }} />

                <StarsMetadataEditor
                  layerMetadata={{
                    ...DEFAULT_STARS_METADATA,
                    ...(typeof watch('layerMetadata') === 'object' &&
                    !Array.isArray(watch('layerMetadata'))
                      ? watch('layerMetadata')
                      : {}),
                    ...(typeof watch('starMetadata') === 'object' &&
                    !Array.isArray(watch('starMetadata'))
                      ? watch('starMetadata')
                      : {}),
                    // CRITICAL FIX: Include musical style from starMetadata in form data
                    ...(watch('starMetadata')?.musicalStyle && {
                      musicalStyle: watch('starMetadata')?.musicalStyle,
                    }),
                  }}
                  isEditable={true}
                  // CRITICAL FIX: Pass baseAssetData for inheritance support
                  baseAssetData={(() => {
                    const starMeta = watch('starMetadata');
                    const layerMeta = watch('layerMetadata');
                    const baseAssetData =
                      starMeta?.baseAssetData || layerMeta?.baseAssetData;
                    if (baseAssetData) {
                      console.log(
                        '🔄 [INHERITANCE] Passing baseAssetData to StarsMetadataEditor:',
                        baseAssetData
                      );
                      return baseAssetData;
                    }
                    return undefined;
                  })()}
                  onMetadataChange={updatedMetadata => {
                    console.log(
                      '🔧 [REVIEW DETAILS] StarsMetadataEditor metadata changed:',
                      updatedMetadata
                    );
                    // ANTI-BLOAT: For S layer, ONLY set starMetadata (no layerMetadata to prevent dual metadata)
                    setValue('starMetadata', updatedMetadata);
                  }}
                />
              </>
            )}

            {/* Looks Layer Enhanced Metadata - Looks Information */}
            {watchLayer === 'L' && (
              <>
                <Divider sx={{ my: 3 }} />

                <LooksInformation
                  asset={{
                    looksMetadata: watch('looksMetadata'),
                    aiMetadata: watch('aiMetadata'),
                  }}
                  isEditable={true}
                  onMetadataChange={updatedMetadata => {
                    console.log(
                      '🔧 [REVIEW DETAILS] LooksInformation metadata changed:',
                      updatedMetadata
                    );
                    setValue('looksMetadata', updatedMetadata);
                  }}
                />
              </>
            )}

            {/* Moves Layer Enhanced Metadata */}
            {watchLayer === 'M' && (
              <>
                <Divider sx={{ my: 3 }} />

                <MovesMetadataEditor
                  layerMetadata={
                    watch('layerMetadata') || watch('movesMetadata') || {}
                  }
                  asset={{
                    movesMetadata: watch('movesMetadata'),
                    aiMetadata: watch('aiMetadata'),
                  }}
                  isEditable={true}
                  onMetadataChange={updatedMetadata => {
                    console.log(
                      '🔧 [REVIEW DETAILS] MovesMetadataEditor metadata changed:',
                      updatedMetadata
                    );
                    // ANTI-BLOAT: For M layer, ONLY set layerMetadata (no movesMetadata to prevent dual metadata)
                    setValue('layerMetadata', updatedMetadata);
                  }}
                />
              </>
            )}

            {/* Worlds Layer Enhanced Metadata */}
            {watchLayer === 'W' && (
              <>
                <Divider sx={{ my: 3 }} />

                <WorldsMetadataEditor
                  layerMetadata={
                    watch('layerMetadata') || watch('worldsMetadata') || {}
                  }
                  asset={{
                    worldsMetadata: watch('worldsMetadata'),
                    aiMetadata: watch('aiMetadata'),
                  }}
                  isEditable={true}
                  onMetadataChange={updatedMetadata => {
                    console.log(
                      '🔧 [REVIEW DETAILS] WorldsMetadataEditor metadata changed:',
                      updatedMetadata
                    );
                    // ANTI-BLOAT: For W layer, ONLY set layerMetadata (no worldsMetadata to prevent dual metadata)
                    setValue('layerMetadata', updatedMetadata);
                  }}
                />
              </>
            )}

            {/* Composite Layer Enhanced Metadata */}
            {watchLayer === 'C' && (
              <>
                <Divider sx={{ my: 3 }} />

                <CompositeMetadataEditor
                  layerMetadata={
                    watch('layerMetadata') || watch('compositeMetadata') || {}
                  }
                  asset={{
                    compositeMetadata: watch('compositeMetadata'),
                    aiMetadata: watch('aiMetadata'),
                  }}
                  componentAssets={(() => {
                    const values = getValues() as unknown as Record<string, unknown>;
                    const c = values.components ?? (values.layerSpecificData as { components?: unknown[] } | undefined)?.components;
                    if (Array.isArray(c) && c.length >= 5) {
                      return { songs: c[0], stars: c[1], looks: c[2], moves: c[3], worlds: c[4] };
                    }
                    return {};
                  })()}
                  isEditable={true}
                  onMetadataChange={updatedMetadata => {
                    console.log(
                      '🔧 [REVIEW DETAILS] CompositeMetadataEditor metadata changed:',
                      updatedMetadata
                    );
                    // ANTI-BLOAT: For composite layer, ONLY set layerMetadata (no compositeMetadata to prevent dual metadata)
                    setValue('layerMetadata', updatedMetadata);
                  }}
                />
              </>
            )}

            {/* Asset Actions and Metadata Summary */}
            <Box
              sx={{
                mt: 4,
                p: 3,
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                backgroundColor: '#f8f9fa',
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                📋 Asset Summary
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Review your asset details above. When ready, proceed to register
                your asset or make additional changes.
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Composite Selection Dialog */}
      {showCompositeDialog && (
        <Dialog
          open={showCompositeDialog}
          onClose={() => setShowCompositeDialog(false)}
          maxWidth="lg"
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              width: '90vw',
              maxWidth: '1200px',
              height: '80vh',
              maxHeight: '800px',
            },
          }}
        >
          <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              🎵 Select Component Assets
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0, height: '100%' }}>
            <CompositeAssetSelection
              onComponentsSelected={selectedComponents => {
                environmentSafeLog(
                  '🚨🚨🚨 [COMPOSITE SELECTION] Components selected in ReviewDetailsStep:',
                  selectedComponents
                );
                setValue('layerSpecificData', {
                  components: selectedComponents,
                });
                setSelectedCompositeAssets(selectedComponents);
                setShowCompositeDialog(false);
              }}
              initialComponents={selectedCompositeAssets}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Base Star Selection Modal for Stars Layer */}
      {showBaseStarModal && (
        <Dialog
          open={showBaseStarModal}
          onClose={() => setShowBaseStarModal(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ borderBottom: '1px solid #e0e0e0', pb: 2 }}>
            Select Base Star
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            {loadingBaseStars ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <CircularProgress size={40} />
                <Typography sx={{ mt: 2 }}>
                  Loading available base stars...
                </Typography>
              </Box>
            ) : availableBaseStars.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  No base stars found in your recent registrations
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Register a base star first, then create variants
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {availableBaseStars.map((star, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      borderBottom:
                        index < availableBaseStars.length - 1
                          ? '1px solid #f0f0f0'
                          : 'none',
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                    }}
                    onClick={() => {
                      environmentSafeLog(
                        '🔍 [BASE STARS] Selected star from modal:',
                        star.hfn
                      );
                      const currentStarMetadata = watch('starMetadata') || {};
                      setValue('starMetadata', {
                        ...currentStarMetadata,
                        baseStarId: star.hfn,
                      });
                      setShowBaseStarModal(false);
                      environmentSafeLog(
                        '✅ [BASE STARS] Base star selection applied'
                      );
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <Typography variant="body1" fontWeight="medium">
                            {star.starName || 'Unnamed Star'}
                          </Typography>
                          <Chip
                            label={star.hfn}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          {star.archetype} • {star.gender} • Registered:{' '}
                          {new Date(star.registrationDate).toLocaleDateString()}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </DialogContent>
          <DialogActions sx={{ borderTop: '1px solid #e0e0e0', p: 2 }}>
            <Button onClick={() => setShowBaseStarModal(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default ReviewDetailsStep;
